import { useCallback, useMemo } from "react"
import type { CombinedError } from "urql"
import { useMutation, useQuery } from "urql"

import type {
  Comment,
  CommentListSuccess,
  CommentMutationCreateArgs,
  CommentMutationUpdateArgs,
  Mutation,
  Query,
} from "@/graphql/codegen/graphql"
import {
  CommentListQueryDocument,
  CommentParentTableType,
  CreateCommentMutationDocument,
  UpdateCommentMutationDocument,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

export const useCommentsActions = (
  referenceId?: string,
  type?: CommentParentTableType
): {
  commentList: Comment[]
  commentListFetching: boolean
  commentListError: CombinedError | undefined
  createComment: (input: CommentMutationCreateArgs) => Promise<void>
  refetchCommentList: () => void
  updateComment: (input: CommentMutationUpdateArgs) => Promise<void>
} => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)

  const [{ data, fetching: commentListFetching, error: commentListError }, reexecuteCommentListQuery] = useQuery<
    Pick<Query, "comment">
  >({
    query: CommentListQueryDocument,
    variables: { referenceId, parentTableType: type ?? CommentParentTableType.Company },
    pause: !referenceId,
  })

  const refetchCommentList = useCallback(() => {
    reexecuteCommentListQuery({ requestPolicy: "network-only" })
  }, [reexecuteCommentListQuery])

  const [, create] = useMutation<Pick<Mutation, "comment">, CommentMutationCreateArgs>(CreateCommentMutationDocument)

  const [, update] = useMutation<Pick<Mutation, "comment">, CommentMutationUpdateArgs>(UpdateCommentMutationDocument)

  const commentList = useMemo(() => {
    return (
      (data?.comment?.list as CommentListSuccess)?.comments?.sort((a, b) => {
        const dateA = new Date(a.updatedOn || a.createdOn!).getTime()
        const dateB = new Date(b.updatedOn || b.createdOn!).getTime()
        return dateB - dateA // Sort in descending order (most recent first)
      }) ?? []
    )
  }, [data?.comment])

  const createComment = async (input: CommentMutationCreateArgs) => {
    const { data, error } = await create(input)
    const hasError = error?.message || data?.comment.create.__typename === "CommentCreateFailure"

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? "Failed to create a comment",
      })
      return
    }

    notify({
      type: NotificationType.Success,
      silent: false,
      content: "Comment successfully created",
    })
  }
  const updateComment = async (input: CommentMutationUpdateArgs) => {
    const { data, error } = await update(input)
    const hasError = error?.message || data?.comment.update.__typename === "CommentUpdateFailure"

    if (hasError) {
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? "Failed to update a comment",
      })
      return
    }

    notify({
      type: NotificationType.Success,
      silent: false,
      content: "Comment successfully updated",
    })
  }

  return {
    commentList,
    commentListFetching,
    commentListError,
    createComment,
    updateComment,
    refetchCommentList,
  }
}
